import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  AlertTriangle, Bell, CheckCircle2, RefreshCw, Building2,
  Shield, DollarSign, Wrench, Calendar, Pill, Users, FileWarning, Filter, XCircle
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import DocumentAlertsSection from './DocumentAlertsSection';

const SEVERITY_COLORS = {
  HIGH: 'bg-red-500 text-white',
  MEDIUM: 'bg-amber-500 text-white',
  LOW: 'bg-slate-400 text-white',
};

// Updated to match the new backend alert types
const ALERT_TYPES = [
  'FUNDING_LOW',
  'PLAN_EXPIRING',
  'COMPLIANCE_EXPIRING',
  'CARE_PLAN_REVIEW_DUE',
  'RISK_REVIEW_DUE',
  'MEDICATION_MISSED',
  'UNASSIGNED_SHIFT',
  'PAYROLL_EXCEPTION',
  'CLAIM_REJECTED'
];

const ALERT_TYPE_ICONS = {
  FUNDING_LOW: DollarSign,
  PLAN_EXPIRING: Calendar,
  COMPLIANCE_EXPIRING: Shield,
  CARE_PLAN_REVIEW_DUE: Users,
  RISK_REVIEW_DUE: AlertTriangle,
  MEDICATION_MISSED: Pill,
  UNASSIGNED_SHIFT: Calendar,
  PAYROLL_EXCEPTION: FileWarning,
  CLAIM_REJECTED: XCircle,
};

const ALERT_TYPE_LABELS = {
  FUNDING_LOW: 'Funding Low',
  PLAN_EXPIRING: 'Plan Expiring',
  COMPLIANCE_EXPIRING: 'Compliance Expiring',
  CARE_PLAN_REVIEW_DUE: 'Care Plan Review Due',
  RISK_REVIEW_DUE: 'Risk Review Due',
  MEDICATION_MISSED: 'Medication Missed',
  UNASSIGNED_SHIFT: 'Unassigned Shift',
  PAYROLL_EXCEPTION: 'Payroll Exception',
  CLAIM_REJECTED: 'Claim Rejected',
};

function AlertCard({ alert, selected, onSelect, onResolve }) {
  const Icon = ALERT_TYPE_ICONS[alert.alertType] || Bell;
  const isResolved = alert.status === 'RESOLVED';

  return (
    <Card 
      data-testid={`alert-card-${alert.id}`}
      className={`${isResolved ? 'opacity-60' : ''} ${selected ? 'ring-2 ring-primary' : ''}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selected}
            onCheckedChange={() => onSelect(alert.id)}
            disabled={isResolved}
            data-testid={`alert-checkbox-${alert.id}`}
          />
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            alert.severity === 'HIGH' ? 'bg-red-500/10' :
            alert.severity === 'MEDIUM' ? 'bg-amber-500/10' :
            'bg-slate-500/10'
          }`}>
            <Icon className={`h-5 w-5 ${
              alert.severity === 'HIGH' ? 'text-red-500' :
              alert.severity === 'MEDIUM' ? 'text-amber-500' :
              'text-slate-500'
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium">{ALERT_TYPE_LABELS[alert.alertType] || alert.alertType}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{alert.message}</p>
              </div>
              <Badge className={SEVERITY_COLORS[alert.severity]}>
                {alert.severity}
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
              <span>{new Date(alert.createdAt).toLocaleString()}</span>
              <Badge variant="outline" className="text-xs">{alert.entityType}</Badge>
              {isResolved && (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  Resolved {alert.resolvedAt && `on ${new Date(alert.resolvedAt).toLocaleDateString()}`}
                </span>
              )}
              {alert.participant_name && alert.participant_name.trim() && (
                <span className="text-blue-600">Participant: {alert.participant_name}</span>
              )}
              {alert.staff_name && alert.staff_name.trim() && (
                <span className="text-purple-600">Staff: {alert.staff_name}</span>
              )}
            </div>
          </div>
          {!isResolved && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResolve(alert.id)}
              data-testid={`resolve-alert-${alert.id}`}
            >
              Resolve
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AlertsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertCounts, setAlertCounts] = useState({});
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');
  const [selectedSeverity, setSelectedSeverity] = useState(searchParams.get('severity') || '');
  const [selectedType, setSelectedType] = useState(searchParams.get('alertType') || '');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'ACTIVE');
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [docAlertsFlagEnabled, setDocAlertsFlagEnabled] = useState(false);
  const canViewDocs = (user?.permissions || []).includes('participants.view_documents');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page: 1, pageSize: 50 };
      if (selectedBranch) params.branchId = selectedBranch;
      if (selectedSeverity) params.severity = selectedSeverity;
      if (selectedType) params.alertType = selectedType;
      if (selectedStatus) params.status = selectedStatus;

      const [alertsRes, countsRes, branchRes] = await Promise.all([
        api.get('/alerts', { params }),
        api.get('/alerts/count', { params: { branchId: selectedBranch } }),
        api.get('/org/branches'),
      ]);
      
      setAlerts(alertsRes.data.items || []);
      setAlertCounts(countsRes.data.data || {});
      setBranches(branchRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch, selectedSeverity, selectedType, selectedStatus]);

  // Fetch feature flag for document alerts
  useEffect(() => {
    api.get('/production/feature-flags/mine')
      .then(r => {
        const flags = r?.data?.data || {};
        setDocAlertsFlagEnabled(!!flags.participant_document_alerts_enabled);
      })
      .catch(() => setDocAlertsFlagEnabled(false));
  }, []);

  const showDocAlerts = docAlertsFlagEnabled && canViewDocs;

  const handleResolve = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      fetchData();
    } catch (err) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleBulkResolve = async () => {
    if (selectedAlerts.length === 0) return;
    try {
      await api.post('/alerts/bulk-resolve', selectedAlerts);
      toast.success(`${selectedAlerts.length} alerts resolved`);
      setSelectedAlerts([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to resolve alerts');
    }
  };

  const handleGenerateAlerts = async () => {
    setGenerating(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const res = await api.post('/alerts/generate', null, { params });
      const created = res.data.data?.total_created || 0;
      if (created > 0) {
        toast.success(`Generated ${created} new alerts`);
      } else {
        toast.info('No new alerts generated - all conditions are within thresholds');
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelectAlert = (alertId) => {
    setSelectedAlerts(prev => 
      prev.includes(alertId) 
        ? prev.filter(id => id !== alertId)
        : [...prev, alertId]
    );
  };

  const selectAll = () => {
    const active = alerts.filter(a => a.status === 'ACTIVE').map(a => a.id);
    setSelectedAlerts(active);
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">System Alerts</h1>
          <p className="text-muted-foreground">Monitor and manage system-wide alerts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleGenerateAlerts}
            disabled={generating}
            data-testid="generate-alerts-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Scanning...' : 'Scan for Alerts'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={alertCounts.HIGH > 0 ? 'border-red-500/50 bg-red-500/5' : ''}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{alertCounts.HIGH || 0}</p>
            <p className="text-xs text-muted-foreground">High Priority</p>
          </CardContent>
        </Card>
        <Card className={alertCounts.MEDIUM > 0 ? 'border-amber-500/50 bg-amber-500/5' : ''}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{alertCounts.MEDIUM || 0}</p>
            <p className="text-xs text-muted-foreground">Medium</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{alertCounts.LOW || 0}</p>
            <p className="text-xs text-muted-foreground">Low</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{alertCounts.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total Active</p>
          </CardContent>
        </Card>
      </div>

      {/* Document Alerts Section (feature-flag gated) */}
      {showDocAlerts && <DocumentAlertsSection />}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            
            <Select value={selectedBranch || 'all'} onValueChange={v => setSelectedBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="branch-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedSeverity || 'all'} onValueChange={v => setSelectedSeverity(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px]" data-testid="severity-filter">
                <SelectValue placeholder="All Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedType || 'all'} onValueChange={v => setSelectedType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px]" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ALERT_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{ALERT_TYPE_LABELS[type] || type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[140px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="RESOLVED">Resolved</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex-1" />
            
            {selectedAlerts.length > 0 && (
              <Button onClick={handleBulkResolve} data-testid="bulk-resolve-btn">
                Resolve {selectedAlerts.length} selected
              </Button>
            )}
            {selectedStatus === 'ACTIVE' && alerts.some(a => a.status === 'ACTIVE') && (
              <Button variant="outline" onClick={selectAll} data-testid="select-all-btn">
                Select All
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alert List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-3" />
            <p className="text-lg font-medium">
              {selectedStatus === 'RESOLVED' ? 'No resolved alerts' : 'No active alerts'}
            </p>
            <p className="text-sm text-muted-foreground">
              {selectedStatus === 'RESOLVED' 
                ? 'Resolved alerts will appear here' 
                : 'All systems are operating normally'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map(alert => (
            <AlertCard
              key={alert.id}
              alert={alert}
              selected={selectedAlerts.includes(alert.id)}
              onSelect={toggleSelectAlert}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}
    </div>
  );
}
